import React, { useEffect, useRef } from 'react'

const MAX_FACE_OFFSET = 5

export interface FlipperAvatarProps {
  size?: 'xs' | 's' | 'm'
  className?: string
  overDark?: boolean
  followMouseAnimation?: boolean
  waveAnimation?: boolean
}

const sizeClassMap = { xs: 'w-6 h-6', s: 'w-8 h-8', m: 'w-10 h-10' }

export const FlipperAvatar: React.FC<FlipperAvatarProps> = ({
  size = 's',
  className = '',
  overDark = false,
  followMouseAnimation = false,
  waveAnimation = false
}) => {
  const fillColor = overDark ? '#F9A4D2' : '#F9A8D4'
  const svgRef = useRef<SVGSVGElement>(null)
  const faceRef = useRef<SVGGElement>(null)

  useEffect(() => {
    if (!followMouseAnimation) {
      if (faceRef.current) {
        faceRef.current.setAttribute('transform', '')
      }
      return
    }

    let rafId: number
    let targetX = 0
    let targetY = 0
    let currentX = 0
    let currentY = 0

    const handleMouseMove = (e: MouseEvent) => {
      const svg = svgRef.current
      if (!svg) return

      const rect = svg.getBoundingClientRect()
      const centerX = rect.left + rect.width / 2
      const centerY = rect.top + rect.height / 2

      const dx = e.clientX - centerX
      const dy = e.clientY - centerY
      const dist = Math.sqrt(dx * dx + dy * dy) || 1

      targetX = (dx / dist) * Math.min(MAX_FACE_OFFSET, dist * 0.02)
      targetY = (dy / dist) * Math.min(MAX_FACE_OFFSET, dist * 0.02)
    }

    const animate = () => {
      currentX += (targetX - currentX) * 0.1
      currentY += (targetY - currentY) * 0.1

      if (faceRef.current) {
        faceRef.current.setAttribute(
          'transform',
          `translate(${currentX.toFixed(2)}, ${currentY.toFixed(2)})`
        )
      }

      rafId = requestAnimationFrame(animate)
    }

    window.addEventListener('mousemove', handleMouseMove)
    rafId = requestAnimationFrame(animate)

    return () => {
      window.removeEventListener('mousemove', handleMouseMove)
      cancelAnimationFrame(rafId)
      if (faceRef.current) {
        faceRef.current.setAttribute('transform', '')
      }
    }
  }, [followMouseAnimation])

  return (
    <span
      className={`inline-block shrink-0 rounded-full ${sizeClassMap[size]} ${className}`}
      title='Flipper (AI Assistant)'>
      <svg
        ref={svgRef}
        width='100%'
        height='100%'
        viewBox='9 16 54 54'
        fill='none'
        xmlns='http://www.w3.org/2000/svg'
        className='block overflow-visible'>
        <style>{`
          @keyframes flipper-wave {
            0%, 0.66%  { transform: translate(-40px, 0) rotate(-40deg); }
            0.1%  { transform: rotate(-45deg); }
            0.2%  { transform: rotate(-20deg); }
            0.3%  { transform: rotate(-45deg); }
            0.4%  { transform: rotate(-20deg); }
            0.5%  { transform: rotate(-45deg); }
            0.66%, 100% { transform: translate(-40px, 0) rotate(-40deg); }
          }
          .flipper-fin-wave {
            animation: flipper-wave 300s ease-in-out infinite;
            transform-origin: 0px 10.83px;
            transform: translate(-40px, 0);
          }
        `}</style>
        <rect x='9' y='16' width='54' height='54' rx='27' fill={fillColor} />
        <g transform='translate(56, 42)'>
          <path
            className={waveAnimation ? 'flipper-fin-wave' : undefined}
            d='M0 10.8333C7.02183 11.6667 14.0423 15 17.5533 15C21.0643 15 21.0635 10.8333 15.7978 6.66667C10.5322 2.5 0 0 0 0V10.8333Z'
            fill={fillColor}
            style={
              !waveAnimation ? { transform: 'translate(-40px, 0)' } : undefined
            }
          />
        </g>
        <g ref={faceRef} className='flipper-face'>
          <path
            d='M24.9454 43.001C24.9454 43.001 27.8839 48.5239 35.9911 48.5239C44.0983 48.5239 47.0367 43.001 47.0367 43.001'
            stroke='black'
            strokeWidth='2'
            strokeLinecap='round'
            strokeLinejoin='round'
          />
          <path
            d='M27.7069 40.2393C27.8848 40.0042 28.104 39.7836 28.3245 39.581C30.3752 37.7422 33.2553 36.9722 35.9912 36.9778C35.9914 36.9778 35.9916 36.9778 35.9918 36.9778C38.7275 36.9722 41.6075 37.7419 43.6579 39.5809C43.8784 39.7836 44.0975 40.0041 44.2754 40.2393C44.0499 40.0509 43.795 39.8836 43.5443 39.7332C41.232 38.3888 38.588 37.9851 35.9918 37.9778C35.9916 37.9778 35.9914 37.9778 35.9912 37.9778C33.3949 37.9852 30.7507 38.3892 28.4381 39.7334C28.1874 39.8837 27.9324 40.051 27.7069 40.2393Z'
            fill='#FBCFE8'
          />
          <path
            d='M20.3431 37.4775H20.3611'
            stroke='black'
            strokeWidth='3'
            strokeLinecap='round'
            strokeLinejoin='round'
          />
          <path
            d='M51.6392 37.4775H51.6571'
            stroke='black'
            strokeWidth='3'
            strokeLinecap='round'
            strokeLinejoin='round'
          />
        </g>
      </svg>
    </span>
  )
}

export default FlipperAvatar